import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser, getAgentByEmail, forgotPassword } from "../services/api";
import "../css/Login.css";

function Login({ setUserRole, setUserId, setPasswordChangeRequired }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [agentId, setAgentId] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loginMessage, setLoginMessage] = useState("");
  
  // Add state variables for user role and password change requirement
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentPasswordChangeRequired, setCurrentPasswordChangeRequired] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "AdvisorMitra.com - Login";
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("username");
    localStorage.removeItem("userRole");
    localStorage.removeItem("agentId");
    setUsername("");
    setPassword("");
  }, []);

  useEffect(() => {
    const storedAgentId = localStorage.getItem("agentId");
    if (storedAgentId) {
      setAgentId(storedAgentId);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await loginUser(username, password);

      if (response.status === 200 && response.data) {
        const { token, role, user } = response.data;
        const { username: userEmail, password_change_required, id } = user;

        // Store user data
        localStorage.setItem("token", token);
        localStorage.setItem("userId", id);
        localStorage.setItem("username", userEmail);
        localStorage.setItem("userRole", role);

        setUserRole(role);
        setUserId(id);
        setPasswordChangeRequired(password_change_required);
        
        // Set current state variables
        setCurrentUserRole(role);
        setCurrentPasswordChangeRequired(password_change_required);

        // Set success message
        setLoginMessage("Login successful! Welcome to AdvisorMitra.");
        setShowSuccessModal(true);
        
        // For AGENT role: fetch agent details
        if (role === "AGENT") {
          try {
            const agentResponse = await getAgentByEmail(userEmail);
            if (agentResponse.status === 200) {
              const agentId = agentResponse.data.id;
              localStorage.setItem("agentId", agentId);
            }
          } catch (agentError) {
            console.error("Error fetching agent details:", agentError);
          }
        }
      }
    } catch (error) {
      let errorMessage = "An error occurred during login.";
      
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 401) {
          errorMessage = "Invalid username or password.";
        }
      } else if (error.request) {
        errorMessage = "No response from server. Please check your network connection.";
      }
      
      setLoginMessage(errorMessage);
      setShowSuccessModal(true);
    }
  };

  const handleSuccessContinue = () => {
    setShowSuccessModal(false);
    
    // Use the current state variables
    if (currentPasswordChangeRequired) {
      navigate("/change-password");
    } else if (currentUserRole === "AGENT") {
      navigate("/agentdashboard");
    } else if (currentUserRole === "PRIMARY" || currentUserRole === "SECONDARY") {
      navigate("/ownerdashboard");
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      setLoginMessage("Please enter your email.");
      setShowSuccessModal(true);
      return;
    }

    try {
      const response = await forgotPassword(forgotEmail);
      if (response.status === 200) {
        setLoginMessage("Reset password instructions sent to your email.");
        setShowSuccessModal(true);
        setShowForgotPassword(false);
        setForgotEmail("");
      }
    } catch (error) {
      let errorMessage = "Error sending reset password. Please try again.";
      
      if (error.response && error.response.status === 404) {
        errorMessage = "Email not registered. Please check and try again.";
      }
      
      setLoginMessage(errorMessage);
      setShowSuccessModal(true);
    }
  };

  return (
    <div className="login-container">
      <form className="login-form" onSubmit={handleLogin}>
        <h2>Login</h2>
        <div className="form-group">
          <label htmlFor="username" style={{ color: 'rgb(112, 16, 99)' }}>Username:</label>
          <input
            type="text"
            placeholder="Enter Username"
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="password" style={{ color: 'rgb(112, 16, 99)' }}>Password:</label>
          <input
            type="password"
            placeholder="Enter Password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <button type="submit">Login</button>
        <p
          className="forgot-password-link"
          onClick={() => setShowForgotPassword(true)}
        >
          Forgot Password?
        </p>
      </form>

      {/* Success/Error Modal */}
      {showSuccessModal && (
        <div className="modal-overlay">
          <div className={`modal-content ${loginMessage.includes("success") ? "success-modal" : "error-modal"}`}>
            <h3>{loginMessage.includes("success") ? "Success!" : "Error"}</h3>
            <p>{loginMessage}</p>
            <div className="modal-buttons">
              <button onClick={handleSuccessContinue}>
                {loginMessage.includes("success") ? "Continue" : "Try Again"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Reset Password</h3>
            <input
              type="email"
              placeholder="Enter your registered email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
            />
            <div className="modal-buttons">
              <button onClick={handleForgotPassword}>Send Reset Email</button>
              <button className="cancel-btn" onClick={() => setShowForgotPassword(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
// In this example, we have created a login form component that allows users to log in to the application. The component handles user authentication by making an API call to the server with the provided username and password. Upon successful login, the user's role and ID are stored in local storage for future use. The component also redirects the user based on their role after successful login. If the user is required to change their password, they are redirected to the change password page. If an error occurs during login, an appropriate error message is displayed to the user.
